import React from "react";

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { IconAlertTriangle, IconFullscreen, IconShareForwardBox, IconTag, IconUpload } from "@synthesis/shared-icons";
import {
  getForModel,
  getMarkupReportByModelIds,
  getScreenshotsByModelId,
  getTypicalMarkups,
  getUserInfo,
  type FileMetaDto,
  type MarkupDto,
  type UserDto,
} from "../../../api";
import { Button, Checkbox as CheckboxFromShared, LoadingIndicator, Modal, ScrollArea, useManualModalState } from "@synthesis/shared-ui";
import { getApiBaseUrl } from "@synthesis/shared-services";
import { cn, logger } from "@synthesis/shared-utils";

const reportBlobConfig = { responseType: "blob" as const };

const asReportBlob = (data: unknown): Blob => {
  if (data instanceof Blob) return data;
  throw new Error("Unexpected report response format");
};

const useTypicalMarkupDescription = (typicalMarkupId: string | null) => {
  return useQuery({
    queryKey: ["typicalMarkupDescription", typicalMarkupId],
    queryFn: async () => {
      if (!typicalMarkupId) return null;
      const typicalMarkups = (await getTypicalMarkups({})).data;
      const currentTypicalMarkup = typicalMarkups.find((typicalMarkup) => typicalMarkup.id === typicalMarkupId);
      return currentTypicalMarkup?.description || null;
    },
    enabled: !!typicalMarkupId,
  });
};

type ViewerGGEBlockProps = {
  markup: MarkupDto;
  onClick: () => void;
  screenshot: string;
};

const ViewerGGEBlock = ({ markup, onClick, screenshot }: ViewerGGEBlockProps) => {
  const { data: typicalMarkupDescription, isLoading: isLoadingTypicalMarkup } = useTypicalMarkupDescription(
    markup.isTypical ? (markup.typicalMarkupId ?? null) : null,
  );
  const { data: author, isLoading: isLoadingAuthor } = useQuery({
    queryKey: ["author", markup.authorId],
    queryFn: async () => (await getUserInfo({ path: { userId: markup.authorId } })).data,
    enabled: !!markup.authorId,
  });

  const isLoading = isLoadingTypicalMarkup || isLoadingAuthor;
  if (markup.id && isLoading) {
    return <figure className="bg-grey-300 animate-pulse rounded bg-opacity-30" />;
  }

  const typical = () => {
    if (markup.isProposedAsTypical) {
      return (
        <CheckboxFromShared disabled={true} checked={true} label={<span className="subtitle-base text-type-light">Предложить как типовое</span>} />
      );
    }
    if (markup.isCritical) {
      return (
        <div className="flex items-stretch gap-1">
          <IconAlertTriangle className="text-type-light size-5" />
          <NameMarkup typical number={1} className="text-type-light">
            {typicalMarkupDescription}
          </NameMarkup>
        </div>
      );
    }

    if (markup.isTypical) {
      return (
        <div className="flex items-center space-x-2">
          <IconTag className="size-5" />
          <NameMarkup typical number={1} className="text-type-light">
            {typicalMarkupDescription}
          </NameMarkup>
        </div>
      );
    }

    return null;
  };

  const condition = markup.isProposedAsTypical || markup.isTypical;
  const authorLabel = formatAuthorLabel(author);

  return (
    <div
      key={markup.id}
      onClick={onClick}
      className="flex h-[295px] w-[336px] cursor-pointer flex-col items-center justify-start rounded-xl bg-white"
    >
      <div className="relative h-[193px] w-full">
        <img className="border-primary-dark absolute inset-0 h-full w-full rounded-t-xl border object-cover" src={screenshot} alt={markup.name} />
      </div>
      <div className="flex h-[102px] w-full flex-col gap-2 px-4 pt-2">
        <NameMarkup number={condition ? 1 : 2}>{markup.name}</NameMarkup>
        {typical()}
        {authorLabel && (
          <p className="paragraph-sm text-type-light">
            {authorLabel}, {dayjs(markup.date).format("DD.MM.YYYY, HH:mm")}
          </p>
        )}
      </div>
    </div>
  );
};

const formatAuthorLabel = (author?: UserDto) => {
  if (!author) return "";
  const firstInitial = author.firstName?.[0] ? `${author.firstName[0]}.` : "";
  const middleInitial = author.middleName?.[0] ? `${author.middleName[0]}.` : "";
  return `${author.lastName ?? ""} ${firstInitial}${middleInitial}`.trim();
};

const NameMarkup = ({
  number,
  typical,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { number: number; typical?: boolean }) => (
  <div
    className={cn("subtitle-base overflow-hidden", typical && "flex-1", className)}
    style={{
      WebkitLineClamp: number,
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
    }}
    {...props}
  >
    {children}
  </div>
);

interface AddModalMarkupPreviewProps {
  modelId: string;
  sourceId: string;
  markupsCount?: number;
  isOpen: boolean;
  file?: FileMetaDto;
}

interface ModalMarkupPreviewProps extends AddModalMarkupPreviewProps {
  setIsOpen: (isOpen: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
}

const ModalMarkupPreview = React.memo((props: ModalMarkupPreviewProps) => {
  const { isOpen, modelId, sourceId, markupsCount, file } = props;
  return (
    <Modal.Root manual {...props}>
      <AddModalMarkupPreview modelId={modelId} sourceId={sourceId} markupsCount={markupsCount} isOpen={isOpen} file={file} />
    </Modal.Root>
  );
});

const AddModalMarkupPreview = React.memo(({ modelId, sourceId, markupsCount = 0, isOpen, file }: AddModalMarkupPreviewProps) => {
  const { closeModal } = useManualModalState();

  const [markups, setMarkups] = React.useState<MarkupDto[]>([]);
  const [screenshots, setScreenshots] = React.useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = React.useState(true);
  const [showIFrame, setShowIFrame] = React.useState(false);

  React.useEffect(() => {
    if (isOpen && markupsCount > 0) {
      (async () => {
        try {
          const responseData = (await getForModel({ path: { modelId } })).data;
          const screenshotsData = (await getScreenshotsByModelId({ path: { modelId } })).data;

          setShowIFrame(false);
          setMarkups(responseData);
          setScreenshots(screenshotsData);
          setIsLoading(false);
        } catch (error) {
          logger.error("ModalMarkupPreview", error);
          setIsLoading(false);
          setShowIFrame(true);
        }
      })();
    }
  }, [isOpen, markupsCount, modelId]);

  React.useEffect(() => {
    if (isOpen && markupsCount === 0) {
      setShowIFrame(true);
    }
  }, [isOpen, markupsCount]);

  const url = React.useMemo(() => `${getApiBaseUrl()}/viewer/#/${sourceId}?models=${modelId}`, [modelId, sourceId]);

  const getMarkupReport = async () => {
    if (!file) return;

    const data = asReportBlob((await getMarkupReportByModelIds({ body: [modelId], ...reportBlobConfig })).data);
    const blobUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = blobUrl;
    const now = new Date();
    const datePart = now.toLocaleDateString("ru-RU");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");

    a.download = `Замечания модели ${file.baseName}.ifc ${datePart}, ${hours}:${minutes}.xlsx`;
    document.body.appendChild(a);
    a.click();

    URL.revokeObjectURL(blobUrl);
    document.body.removeChild(a);
  };

  return (
    <Modal.Content className="bg-neutrals-99 h-[80vh] w-full">
      <Modal.Title>{file ? `Файл ${file.baseName}.${file.ext}` : "Замечания модели"}</Modal.Title>
      <figure
        className="absolute right-[35px] top-[12px] z-10 h-5 cursor-pointer items-center px-4 text-black transition-all duration-150"
        onClick={() => {
          window.open(url, "_blank")?.focus();
          closeModal();
        }}
      >
        <IconFullscreen className="text-neutrals-50 size-5" />
      </figure>
      {showIFrame ? (
        <div className="h-[80vh]">
          <iframe title="Model preview" width="100%" height="100%" src={url} sandbox="allow-scripts" />
        </div>
      ) : isLoading ? (
        <div className="flex h-[50vh] w-full items-center justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        markupsCount > 0 && (
          <div className="p-6">
            <div className="flex justify-between pb-4">
              <span>Замечания модели</span>
              <div className="flex gap-2">
                <Button disabled leftIcon={<IconShareForwardBox className="size-5" />} size="sm">
                  Передать...
                </Button>
                <Button onClick={getMarkupReport} leftIcon={<IconUpload className="size-5" />} size="sm">
                  Выгрузить в .xlsx
                </Button>
              </div>
            </div>
            <ScrollArea className="h-[60vh]">
              <div className="grid-cols-[repeat(auto-fill, minmax(280px, 4fr))] grid grid-flow-row gap-4">
                {markups.map((markup) => (
                  <div className="flex items-start justify-center" key={markup.id}>
                    <ViewerGGEBlock
                      onClick={() => {
                        if (!markup.id) return;
                        window.open(`${getApiBaseUrl()}/viewer/#/${sourceId}?models=${modelId}&markup=${markup.id}`, "_blank")?.focus();
                        closeModal();
                      }}
                      markup={markup}
                      screenshot={markup.id ? (screenshots[markup.id] ?? "") : ""}
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )
      )}
    </Modal.Content>
  );
});

export default ModalMarkupPreview;
